import { default as Divider } from 'antd/es/divider';

import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';
import { hasValue } from 'utils/hasValue';

import { transformToObject } from '@pages/Payouts/util';
import type { CreditorKeyValuePair, CreditorWithDetails } from 'api';

import { DetailsItem } from '@atoms/DetailsItem/DetailsItem';

import { Country } from '@molecules/Country';

import { ATTRIBUTES_TO_SHOW } from './constants';
import { ReceiverKeyValuePairs } from './ReceiverKeyValuePairs';

interface Props {
  receiverData: CreditorWithDetails;
}

export const ReceiverDetailsList = ({ receiverData }: Props) => {
  // filter out stuff we dont want to show in the details view like id
  // sort them in a way that follows the design
  // map over the remaining values
  return Object.entries(receiverData)
    .filter(
      (a) => ATTRIBUTES_TO_SHOW.includes(a[0]) && hasValue(a[1]) && a[1] !== ''
    )
    .sort(
      (a, z) =>
        ATTRIBUTES_TO_SHOW.indexOf(a[0]) - ATTRIBUTES_TO_SHOW.indexOf(z[0])
    )
    .map(([key, value]) => {
      // creditorDetails is a CreditorKeyValuePair[], so we handle that separately
      if (key === 'creditor_details') {
        const creditorDetails = transformToObject(
          value as CreditorKeyValuePair[]
        );

        const intermediaryAgent: CreditorKeyValuePair[] =
          creditorDetails.intermediary_agent
            ? Object.entries(creditorDetails.intermediary_agent).map(
                ([key, value]) => ({
                  key: `intermediary_agent.${key}`,
                  value
                })
              )
            : [];

        const intermediaryAgentAccount: CreditorKeyValuePair[] =
          creditorDetails.intermediary_agent_account
            ? Object.entries(creditorDetails.intermediary_agent_account).map(
                ([key, value]) => ({
                  key: `intermediary_agent_account.${key}`,
                  value
                })
              )
            : [];

        const receiverBank: CreditorKeyValuePair[] =
          creditorDetails.creditor_account
            ? Object.entries(creditorDetails.creditor_account).map(
                ([key, value]) => ({
                  key: `creditor_account.${key}`,
                  value: value
                })
              )
            : [];

        return (
          <div key={key}>
            <Divider key="divider-receiver-bank" className="pb-2" />
            {receiverBank.length > 0 && (
              <div>
                <div className="text-lg font-bold">Receiver Bank</div>
                <ReceiverKeyValuePairs value={receiverBank} />
              </div>
            )}
            {intermediaryAgent.length > 0 && (
              <div>
                <Divider key="divider-intermediary-agent" className="pb-2" />
                <div className="pb-2 text-lg font-bold">Intermediary Agent</div>
                <ReceiverKeyValuePairs value={intermediaryAgent} />
              </div>
            )}
            {intermediaryAgentAccount.length > 0 && (
              <div>
                <Divider
                  key="divider-intermediary-agent-account"
                  className="pb-2"
                />
                <div className="text-lg font-bold">
                  Intermediary Agent Account
                </div>
                <ReceiverKeyValuePairs value={intermediaryAgentAccount} />
              </div>
            )}
          </div>
        );
      }
      if (key === 'country_code') {
        return (
          <DetailsItem
            key={key}
            title={fieldToDisplayableName(key, 'RECEIVER_FIELD_NAMES')}
            item={<Country countryIso={value} />}
            classNames="pb-2"
          />
        );
      }
      return (
        <DetailsItem
          key={key}
          title={fieldToDisplayableName(key, 'RECEIVER_FIELD_NAMES')}
          item={value}
          classNames="pb-2"
        />
      );
    });
};
